/**
 * Request payload schemas. The API validates with these; the web app imports the
 * inferred types so a route and its form cannot drift apart.
 *
 * The original replied `{code:'400', required_fields:{field:'Required field'}}`
 * and the UI painted those field names red. `fieldErrors` in the error envelope
 * keeps that contract — see `errors.ts`.
 */

import { z } from 'zod';
import {
  AUTO_RESPONSE_CONDITIONS,
  AUTO_RESPONSE_EQUATIONS,
  AUTO_RESPONSE_SOURCES,
  BLACKLIST_KEYS,
  FIELD_KINDS,
  MAX_FIELD_OPTIONS,
  MAX_FIELDS_PER_WORKSPACE,
  MAX_ITEM_IMAGES,
  MAX_ITEMS_PER_IMPORT,
  RESERVED_FIELD_KEYS,
  ROLES,
  RULE_OPS,
  TEMPLATE_TYPES,
} from './constants.ts';

const email = z.string().trim().toLowerCase().email('Enter a valid email address');
const password = z.string().min(8, 'Use at least 8 characters').max(200);
const uuid = z.string().uuid();
const nonEmpty = (label: string) => z.string().trim().min(1, `${label} is required`);

/**
 * A boolean that arrives in a query string.
 *
 * `z.coerce.boolean()` is the wrong tool here and fails in the most damaging
 * direction: it applies JavaScript's `Boolean()`, so the string `"false"` —
 * exactly what a client sends for a switched-off filter — becomes `true`.
 * Only the literal spellings are accepted, and anything else is a validation
 * error rather than a silent `true`.
 */
const queryBoolean = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((value) => value === true || value === 'true' || value === '1');

// --- auth -------------------------------------------------------------------

export const loginSchema = z.object({
  email,
  password: z.string().min(1, 'Password is required'),
  remember: z.boolean().optional().default(true),
});

export const registerSchema = z.object({
  fullname: nonEmpty('Full name').max(120),
  email,
  password,
});

export const requestPasswordResetSchema = z.object({ email });

export const performPasswordResetSchema = z.object({
  uuid,
  password,
});

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: password,
    verifyPassword: z.string().min(1, 'Confirm your new password'),
  })
  .refine((value) => value.newPassword === value.verifyPassword, {
    message: 'Your new and confirmation password do not match',
    path: ['verifyPassword'],
  });

export const activateAgentSchema = z.object({
  uuid,
  fullname: nonEmpty('Full name').max(120),
  password,
});

export const updateProfileSchema = z.object({
  fullname: nonEmpty('Full name').max(120),
  title: z.string().trim().max(120).optional(),
  avatar: z.string().max(2_000_000).optional(),
});

// --- customer entry ---------------------------------------------------------

export const startCustomerSessionSchema = z.object({
  chatUuid: uuid,
  customerName: nonEmpty('Name').max(120),
  customerEmail: email.optional().or(z.literal('')),
  customerPhone: z.string().trim().max(32).optional(),
  /** Page the widget was opened from; surfaced to the agent as context. */
  pageUrl: z.string().trim().max(2000).optional(),
});

// --- conversations ----------------------------------------------------------

export const listSessionsSchema = z.object({
  search: z.string().trim().max(200).optional(),
  archived: queryBoolean.optional(),
  spam: queryBoolean.optional(),
  assignedTo: z.coerce.number().int().optional(),
  take: z.coerce.number().int().min(1).max(200).optional().default(50),
  cursor: z.coerce.number().int().optional(),
});

export const listMessagesSchema = z.object({
  sessionUuid: uuid,
  before: z.coerce.number().int().optional(),
  take: z.coerce.number().int().min(1).max(200).optional().default(50),
});

export const attachmentSchema = z.object({
  url: z.string().trim().url().max(2000),
  thumbnailUrl: z.string().trim().url().max(2000).optional(),
  width: z.number().int().min(0).max(20_000).optional(),
  height: z.number().int().min(0).max(20_000).optional(),
  name: z.string().trim().max(255).optional(),
});

/** Cap per message, matching the multipart upload limit. */
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;

export const sendMessageSchema = z
  .object({
    sessionUuid: uuid,
    // May be empty when the message is nothing but images.
    content: z.string().max(20_000).default(''),
    attachments: z.array(attachmentSchema).max(MAX_ATTACHMENTS_PER_MESSAGE).default([]),
    prefer: z.enum(['sms', 'email']).optional(),
  })
  .refine((value) => value.content.trim().length > 0 || value.attachments.length > 0, {
    message: 'Write a message or attach an image',
    path: ['content'],
  });

export const assignSessionSchema = z.object({
  /** null releases the conversation back to the unassigned queue. */
  assignedToId: z.number().int().nullable(),
});

export const updateSessionSchema = z.object({
  archived: z.boolean().optional(),
  spam: z.boolean().optional(),
  assignedToId: z.number().int().nullable().optional(),
  customerName: z.string().trim().max(120).optional(),
  customerEmail: email.nullable().optional(),
  customerPhone: z.string().trim().max(32).nullable().optional(),
});

// --- team -------------------------------------------------------------------

export const inviteAgentSchema = z.object({
  email,
  role: z.enum(['admin', 'agent']),
});

// --- templates, engagements, forms ------------------------------------------

export const updateTemplateSchema = z.object({
  type: z.enum(TEMPLATE_TYPES),
  body: nonEmpty('Body').max(10_000),
});

export const engagementSchema = z.object({
  title: nonEmpty('Title').max(200),
  status: z.enum(['enabled', 'disabled']).default('enabled'),
  type: z.string().trim().min(1).max(32).default('popup'),
  markup: z.unknown().default({}),
  // An array, not an object: the widget iterates these to decide when to show
  // the prompt. Defaulting to `{}` shipped a value that `.find()` throws on.
  conditions: z.array(z.unknown()).default([]),
});

export const trackedFormSchema = z.object({
  title: nonEmpty('Title').max(200),
  url: z.string().trim().url('Enter a valid URL'),
  index: z.number().int().min(0).default(0),
  captureOtherPages: z.boolean().default(false),
  captureFormsWithSameFields: z.boolean().default(false),
  source: z.unknown().default([]),
  mapFields: z.unknown().default({}),
  conditions: z.unknown().default([]),
  meta: z.unknown().default({}),
});

export const scanFormSchema = z.object({
  url: z.string().trim().url('Enter a valid URL'),
});

// --- settings ---------------------------------------------------------------

/** #rrggbb only — the widget interpolates it into CSS, so nothing else gets in. */
export const hexColor = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Use a colour like #7958f8');

export const integrationSettingsSchema = z
  .object({
    showChatIcon: z.boolean(),
    askEmail: z.boolean(),
    askPhone: z.boolean(),
    widgetColor: hexColor.default('#7958f8'),
  })
  // With both off the widget's pre-chat form has no contact field to render,
  // so no visitor can ever open a conversation and the server would reject the
  // attempt anyway. Refuse the configuration rather than let it be saved.
  .refine((value) => value.askEmail || value.askPhone, {
    message: 'Ask for an email address, a phone number, or both',
    path: ['askEmail'],
  });

export const archivingSettingsSchema = z.object({
  days: z.number().int().min(1).max(3650),
  ongoingChats: z.boolean(),
  newChats: z.boolean(),
});

/** Workspace-wide notification switches. */
export const notificationSettingsSchema = z.object({
  notifyAgentsWhenOffline: z.boolean(),
  notifyCapturedForms: z.boolean(),
  notifyUnanswered: z.boolean(),
});

export const craigslistSettingsSchema = z.object({
  enabled: z.boolean(),
  username: z
    .string()
    .trim()
    .min(1, 'Required field')
    .max(64)
    .regex(/^[A-Za-z0-9-]+$/, 'Allowed only [A-Z, a-z, 0-9, -] characters'),
  domain: z.string().trim().max(128),
});

export const twilioSettingsSchema = z.object({
  // Twilio account SIDs are "AC" and 32 hex characters, always. Checking the
  // shape is what turns a browser autofilling an email address here into a
  // message naming the field, rather than a saved setting that fails silently
  // the next time someone texts in.
  accountSid: nonEmpty('Account SID')
    .max(64)
    .regex(/^AC[0-9a-fA-F]{32}$/, 'A Twilio account SID looks like AC then 32 characters'),
  authToken: nonEmpty('Auth token').max(128),
  // E.164: a plus and up to fifteen digits.
  phoneNumber: nonEmpty('Phone number')
    .max(32)
    .regex(/^\+[1-9]\d{6,14}$/, 'Use the international form, like +15551234567'),
});

export const chatPreferencesSchema = z.object({
  sendWithEnter: z.boolean(),
  soundEnabled: z.boolean().optional(),
  /** 0-100, scaled into the tone's gain by the player. */
  soundVolume: z.number().int().min(0).max(100).optional(),
  theme: z.enum(['system', 'light', 'dark']).optional(),
});

export const autoResponseSchema = z.object({
  enabled: z.boolean().default(true),
  source: z.enum(AUTO_RESPONSE_SOURCES).default('any'),
  condition: z.enum(AUTO_RESPONSE_CONDITIONS).default('message'),
  equation: z.enum(AUTO_RESPONSE_EQUATIONS).default('contains'),
  phrase: nonEmpty('Phrase').max(200),
  response: nonEmpty('Response').max(10_000),
  autoAssignToId: z.number().int().nullable().default(null),
});

export const presetResponseSchema = z.object({
  title: nonEmpty('Title').max(200),
  content: nonEmpty('Content').max(10_000),
  shortcut: z.string().trim().max(32).nullable().default(null),
  position: z.number().int().min(0).default(0),
});

export const incomingEmailSchema = z.object({
  username: z
    .string()
    .trim()
    .min(1, 'Required field')
    .max(32)
    .regex(/^[A-Za-z0-9._-]+$/, 'Allowed only [A-Z, a-z, 0-9, ., _, -] characters'),
  domain: nonEmpty('Domain').max(64),
});

export const blacklistEntrySchema = z.object({
  key: z.enum(BLACKLIST_KEYS),
  value: nonEmpty('Value').max(200),
});

// --- inventory --------------------------------------------------------------

/**
 * A field key is an identifier, not a label: it goes into JSON keys, query
 * parameters (`f.make=Honda`) and template placeholders (`{{make}}`), so it is
 * restricted to what all three can carry unescaped. Labels are free text.
 */
export const fieldKey = z
  .string()
  .trim()
  .min(1, 'Key is required')
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/, 'Use lowercase letters, digits and underscores, starting with a letter')
  .refine(
    (value) => !(RESERVED_FIELD_KEYS as readonly string[]).includes(value),
    'That key is reserved — pick another',
  );

export const fieldSchema = z
  .object({
    key: fieldKey,
    label: nonEmpty('Label').max(80),
    kind: z.enum(FIELD_KINDS).default('text'),
    required: z.boolean().default(false),
    searchable: z.boolean().default(true),
    filterable: z.boolean().default(false),
    isTitle: z.boolean().default(false),
    options: z.array(z.string().trim().min(1).max(120)).max(MAX_FIELD_OPTIONS).default([]),
    position: z.number().int().min(0).max(1000).optional(),
    /** Percent of the form row. Free-form here; the UI offers presets. */
    width: z.number().int().min(10).max(100).default(100),
  })
  .refine((field) => field.kind !== 'select' || field.options.length > 0, {
    message: 'A choice field needs at least one option',
    path: ['options'],
  })
  .refine((field) => !field.isTitle || field.kind === 'text' || field.kind === 'number', {
    // The title is rendered into message bodies and list rows; a date makes a
    // poor headline.
    message: 'Only a text or number field can be the title',
    path: ['isTitle'],
  });

/**
 * Editing a field cannot change its `key` — that is what every stored item,
 * rule criterion and sidecar value row is keyed on. Renaming edits the label.
 */
export const fieldPatchSchema = z.object({
  label: nonEmpty('Label').max(80).optional(),
  required: z.boolean().optional(),
  searchable: z.boolean().optional(),
  filterable: z.boolean().optional(),
  isTitle: z.boolean().optional(),
  options: z.array(z.string().trim().min(1).max(120)).max(MAX_FIELD_OPTIONS).optional(),
  position: z.number().int().min(0).max(1000).optional(),
  width: z.number().int().min(10).max(100).optional(),
  archived: z.boolean().optional(),
});

export const reorderFieldsSchema = z.object({
  keys: z.array(fieldKey).min(1).max(MAX_FIELDS_PER_WORKSPACE),
});

/**
 * The envelope only. What may go in `data` depends on the fields this workspace
 * has declared, so it is checked at runtime against them — see
 * `validateItemData` in the API. Values are capped here so a malformed feed
 * cannot put a megabyte in one cell before that check runs.
 */
export const itemValue = z.union([
  z.string().max(2000),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.string().max(500)).max(200),
]);

export const itemSchema = z.object({
  externalId: z.string().trim().min(1).max(120).nullable().default(null),
  data: z.record(z.string().max(40), itemValue).default({}),
  images: z.array(z.string().trim().max(1000)).max(MAX_ITEM_IMAGES).default([]),
});

/** Absent keys stay absent, so a one-cell edit stays a one-cell edit. */
export const itemPatchSchema = z.object({
  externalId: z.string().trim().min(1).max(120).nullable().optional(),
  data: z.record(z.string().max(40), itemValue).optional(),
  images: z.array(z.string().trim().max(1000)).max(MAX_ITEM_IMAGES).optional(),
});

export const importItemsSchema = z.object({
  items: z.array(itemSchema).max(MAX_ITEMS_PER_IMPORT),
  /**
   * Stamps every row written by this call. With `mode: "replace"` anything in
   * the workspace not carrying the batch afterwards is soft-deleted — that is
   * how "clear and refill" happens without a window where the inventory is
   * empty, and without losing the rows a half-finished feed never reached.
   */
  batchId: z.string().trim().min(1).max(80).optional(),
  mode: z.enum(['merge', 'replace']).default('merge'),
});

/**
 * Filters arrive as flat `f.<key>=<value>` parameters rather than nested
 * brackets: Fastify's default query parser does not decode `f[make]`, and a
 * flat prefix survives `URLSearchParams` on both sides untouched.
 */
export const FILTER_PREFIX = 'f.';

export const listItemsSchema = z
  .object({
    search: z.string().trim().max(200).optional(),
    /**
     * Only the item's own columns. Sorting by a declared field would mean
     * ordering by a joined sidecar row, which Prisma cannot express and SQLite
     * would answer with a scan — it waits until there is a reason for the raw
     * query it needs.
     */
    sort: z.enum(['createdAt', 'title', 'externalId']).default('createdAt'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    includeDeleted: queryBoolean.optional(),
    take: z.coerce.number().int().min(1).max(200).optional().default(50),
    cursor: z.coerce.number().int().optional(),
  })
  .catchall(z.string().max(200));

export const itemRuleCriterionSchema = z.object({
  key: fieldKey,
  op: z.enum(RULE_OPS).default('equals'),
  value: nonEmpty('Value').max(200),
});

export const itemRuleSchema = z.object({
  sessionUuid: uuid,
  customerName: z.string().trim().max(120).default(''),
  customerEmail: email.nullable().default(null),
  customerPhone: z.string().trim().max(32).nullable().default(null),
  /** Empty means "tell me about everything" — deliberately allowed. */
  criteria: z.array(itemRuleCriterionSchema).max(MAX_FIELDS_PER_WORKSPACE).default([]),
});

export const apiKeySchema = z.object({
  /** What it is for. Shown in the list; the only way to tell two keys apart. */
  name: nonEmpty('Name').max(80),
});

export const inventorySettingsSchema = z.object({
  label: nonEmpty('Name').max(40),
  labelSingular: nonEmpty('Singular name').max(40),
  /**
   * How an incoming row is matched to an existing item when the caller sends no
   * externalId — e.g. ["vin", "stock_number"], tried in order.
   */
  identityFieldKeys: z.array(fieldKey).max(4).default([]),
});

// --- inferred types ---------------------------------------------------------

export type LoginInput = z.infer<typeof loginSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
export type ActivateAgentInput = z.infer<typeof activateAgentSchema>;
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
export type StartCustomerSessionInput = z.infer<typeof startCustomerSessionSchema>;
export type SendMessageInput = z.infer<typeof sendMessageSchema>;
export type UpdateSessionInput = z.infer<typeof updateSessionSchema>;
export type InviteAgentInput = z.infer<typeof inviteAgentSchema>;
export type EngagementInput = z.infer<typeof engagementSchema>;
export type TrackedFormInput = z.infer<typeof trackedFormSchema>;
export type IntegrationSettingsInput = z.infer<typeof integrationSettingsSchema>;
export type ArchivingSettingsInput = z.infer<typeof archivingSettingsSchema>;
export type NotificationSettingsInput = z.infer<typeof notificationSettingsSchema>;
export type CraigslistSettingsInput = z.infer<typeof craigslistSettingsSchema>;
export type TwilioSettingsInput = z.infer<typeof twilioSettingsSchema>;
export type ChatPreferencesInput = z.infer<typeof chatPreferencesSchema>;
export type AutoResponseInput = z.infer<typeof autoResponseSchema>;
export type PresetResponseInput = z.infer<typeof presetResponseSchema>;
export type IncomingEmailInput = z.infer<typeof incomingEmailSchema>;
export type FieldInput = z.infer<typeof fieldSchema>;
export type FieldPatchInput = z.infer<typeof fieldPatchSchema>;
export type ItemInput = z.infer<typeof itemSchema>;
export type ItemPatchInput = z.infer<typeof itemPatchSchema>;
export type ImportItemsInput = z.infer<typeof importItemsSchema>;
export type ItemRuleInput = z.infer<typeof itemRuleSchema>;
export type ItemRuleCriterionInput = z.infer<typeof itemRuleCriterionSchema>;
export type InventorySettingsInput = z.infer<typeof inventorySettingsSchema>;
export type ApiKeyInput = z.infer<typeof apiKeySchema>;
export type ROLE = (typeof ROLES)[number];
